import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import _ from "lodash";
import getReferrerFullName from "../services/getReferrerFullName";
import moment from "moment";
import momentTZ from "moment-timezone";
import getCaseManagerById from "../services/getCaseManagerById";
import { TABLE_NAME } from "../constants";
import {
  ADMIN_ROLE,
  APPOINTMENT_TYPE,
  CLIENT_APPOINTMENTS,
  DEFAULT_TIMEZONE,
  WORKFLOW_APPT_STATUS,
} from "../../../constants";
import { formatDateTime } from "../../../utils";
import {
  getGpStatusClasses,
  getGpReportCellDisplayText,
  getReceivedYesStatus,
  getRemainingWeeksToAppointment,
  getTaskStatusClasses,
  renderCreationTimeSection,
  renderIVRIcon,
  renderManualSMSStatus,
} from "../utils";
import { downloadReferralLetter, fetchReferralLetter } from "../../../services/referralLetters";
import { triggerGPReportDownload } from "../../../services/gpReport";
import { getVerifiedOutboundLetters } from "../../../utils/gpReport";
import { getUsers } from "../../../services/auth";
import { isAIReferralInboxEnabled, isGPReportListEnabled } from "../../../infra/featureToggle";
import "./WorkflowTableItem.css";

import { displayDateTime } from "../../../utils/dateDisplay";
const isDataFromPMS = process.env.REACT_APP_DATA_SOURCE === "pms-api";

// Firestore Timestamps expose `toDate`; the OCR pipeline can also write a
// plain string/Date, so normalise both before handing off to moment.
const toJsDate = (value) => (typeof value?.toDate === "function" ? value.toDate() : value);

// `moment(undefined)` resolves to "now" rather than being invalid, so a
// missing/invalid updatedAt must be ruled out before it ever reaches moment,
// not detected via isValid() after the fact.
const toValidMoment = (value) => {
  if (value == null) return null;
  const parsed = moment(toJsDate(value));
  return parsed.isValid() ? parsed : null;
};

const formatSyncedDate = (updatedAt) => displayDateTime(toValidMoment(updatedAt)) || "";

const getSortedSyncedMedicalResults = (medicalResultsByClientId, clientId) => {
  const results = (clientId && medicalResultsByClientId?.[clientId]) || [];
  const completedResults = results.filter((result) => result.status === "COMPLETED");
  const timestamp = (value) => toValidMoment(value)?.valueOf() ?? Number.NEGATIVE_INFINITY;
  return [...completedResults].sort((a, b) => timestamp(b.updatedAt) - timestamp(a.updatedAt));
};

// Reports are linked to the patient, not the workflow, so the full history
// shows on every one of the patient's workflows. Only the recent window is
// shown by default; older reports sit behind an expand button.
const RECENT_SYNC_DAYS = 30;

const splitSyncedResultsByRecency = (results) => {
  const cutoff = momentTZ.tz(DEFAULT_TIMEZONE).subtract(RECENT_SYNC_DAYS, "days");
  const [recent, earlier] = _.partition(results, (result) => {
    const updatedAt = toValidMoment(_.get(result, "updatedAt"));
    return Boolean(updatedAt) && updatedAt.isSameOrAfter(cutoff);
  });
  return { recent, earlier };
};

const SyncedResultItem = ({ result }) => (
  <div className="synced-medical-result-item">
    <div className="has-text-weight-semibold" style={{ overflowWrap: "break-word" }}>
      {_.get(result, "fileId", "")}
    </div>
    <div className="has-text-grey">
      {[_.get(result, "resultType", ""), formatSyncedDate(_.get(result, "updatedAt"))].filter(Boolean).join(" · ")}
    </div>
  </div>
);

const SyncedResultsIndicator = ({ results, openUp }) => {
  const [showEarlier, setShowEarlier] = useState(false);
  const { recent, earlier } = splitSyncedResultsByRecency(results);
  const reportWord = (count) => (count === 1 ? "report" : "reports");

  return (
    <div
      className={`dropdown is-hoverable is-right${openUp ? " is-up" : ""}`}
      onMouseLeave={() => setShowEarlier(false)}
    >
      <div className="dropdown-trigger">
        <span
          className="tag is-light is-rounded synced-results-indicator"
          aria-label={`${recent.length} patient ${reportWord(
            recent.length
          )} synced to OPAU in the last ${RECENT_SYNC_DAYS} days`}
        >
          <span className="icon is-small">
            <i className="fas fa-paperclip" />
          </span>
          <span>{recent.length}</span>
        </span>
      </div>
      <div className="dropdown-menu" onClick={(e) => e.stopPropagation()}>
        <div
          className="synced-medical-results-panel dropdown-content px-3 py-2 is-size-7"
          style={{ minWidth: "280px", maxWidth: "420px" }}
        >
          <p className="has-text-weight-bold mb-2">Patient reports synced to OPAU</p>
          <div className="synced-medical-results-list">
            <p className="synced-medical-results-section-label">Last {RECENT_SYNC_DAYS} days</p>
            {recent.length > 0 ? (
              recent.map((result) => <SyncedResultItem key={result.id} result={result} />)
            ) : (
              <p className="has-text-grey">None in the last {RECENT_SYNC_DAYS} days</p>
            )}
            {showEarlier && earlier.length > 0 && (
              <>
                <p className="synced-medical-results-section-label mt-3">Earlier</p>
                {earlier.map((result) => (
                  <SyncedResultItem key={result.id} result={result} />
                ))}
              </>
            )}
          </div>
          {earlier.length > 0 && (
            <button
              type="button"
              className="button is-light is-small is-radiusless is-fullwidth mt-2"
              onClick={() => setShowEarlier((value) => !value)}
            >
              <span>
                {showEarlier ? "Hide earlier reports" : `Show ${earlier.length} earlier ${reportWord(earlier.length)}`}
              </span>
              <span className="icon is-small">
                <i className={`fas ${showEarlier ? "fa-chevron-up" : "fa-chevron-down"}`} />
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const WorkflowTableItem = ({
  ca,
  tableName = TABLE_NAME.workflow,
  handleClickManageTasks,
  caDataLength,
  activeCaId,
  handleClickRow,
  timeType,
  index,
  clientAppointments,
}) => {
  const practitionerPreferences = useSelector((state) => state.app.practitionerPreferences);
  const specialistProfiles = useSelector((state) => state.app.specialistProfiles);
  const practitioners = useSelector((state) => state.app.practitioners);
  const referrers = useSelector((state) => state.app.referrers);
  const specialistSchedulerDescriptions = useSelector((state) => state.app.specialistSchedulerDescriptions);
  const medicalResultsByClientId = useSelector((state) => state.medicalResults.resultsByClientId);
  const syncedMedicalResults = getSortedSyncedMedicalResults(medicalResultsByClientId, _.get(ca, "client.clientId"));
  const practitionerId = _.get(ca, "practitioner.practitionerId", null);
  const schedule = _.get(specialistSchedulerDescriptions, [practitionerId, "value"], "");
  const [isDownloadingReferral, setIsDownloadingReferral] = useState(false);
  const [isDownloadingGPReport, setIsDownloadingGPReport] = useState(false);

  const remainingWeeksToAppointment = getRemainingWeeksToAppointment(ca);
  const newOrReview = _.get(ca, "appointment_t.is_new_appointment", false) ? "N" : "R";
  const patientSnapformSubmitted = _.get(ca, "patient_snapform_submitted", false);

  const [coordinators, setCoordinators] = useState([]);

  useEffect(() => {
    async function fetchUsers() {
      const users = await getUsers(ADMIN_ROLE);
      setCoordinators(
        (users?.filter((user) => !user.isITStaff && !user.disabled) || []).map((user) => ({
          label: `${user.firstName} ${user.lastName}`,
          value: user.shortName,
        }))
      );
    }

    fetchUsers();
  }, []);

  const isLastWorkflowRow = tableName === TABLE_NAME.workflow && index === caDataLength - 1;
  const taskManagerClassName = `${isLastWorkflowRow ? "is-up " : ""}dropdown is-hoverable is-right`;
  const taskManagerEventProps =
    tableName !== TABLE_NAME.workflow
      ? {}
      : {
          onClick: (e) => handleClickManageTasks(ca.id),
        };

  const preConsultTaskBtnClassName = `task-btn px-2 ${getTaskStatusClasses(_.get(ca, "pre_consult_t.status", ""))}`;
  const preConsultTaskBtnLabel =
    _.find(coordinators, (coordinator) => coordinator.value === _.get(ca, "pre_consult_t.coordinator", false))?.label ||
    "++";

  const renderHospitalColumn = (ca) => {
    const hospitalName = ca.location?.name;
    return <div>{hospitalName}</div>;
  };

  const rowBackgroundClassName = (() => {
    if (CLIENT_APPOINTMENTS.includes(ca.appointment_t?.status) && remainingWeeksToAppointment < 0) {
      return "has-background-danger-light";
    }

    const latest = _.last(_.get(ca, "activities"));
    if (_.isNil(latest)) {
      return "";
    }
    return _.get(latest, "name", null) === "confirm" ? "has-background-success-light" : "has-background-warning-light";
  })();

  const rowProps =
    tableName !== TABLE_NAME.workflow
      ? {}
      : {
          className: `${rowBackgroundClassName}${ca.id === activeCaId ? "is-active" : ""}`,
          onClick: () => handleClickRow(ca.id),
        };

  const handleDownloadReferralLetter = (ca) => async () => {
    const source = isAIReferralInboxEnabled ? "ai_extracted_referrals" : "ocr_clients";
    setIsDownloadingReferral(true);
    const id = ca.referral_letters?.[0]?.id;
    const name = ca.referral_letters?.[0]?.name;
    if (id && name) {
      const fileId = isDataFromPMS ? name : id; // It is file name in gcs /ai-ocr-referral/<file_name> actually
      const res = await fetchReferralLetter(fileId, source);
      downloadReferralLetter([res.data], name);
    }
    setIsDownloadingReferral(false);
  };

  const handleDownloadGPReport = (ca) => async () => {
    setIsDownloadingGPReport(true);
    try {
      const ok = await triggerGPReportDownload(ca.gp_report);
      if (!ok) {
        console.error("GP report id is not provided");
      }
    } catch (error) {
      console.error("Error downloading GP report", error);
    } finally {
      setIsDownloadingGPReport(false);
    }
  };

  const generateExpiredSection = (ca) => {
    const expiredTime = _.get(ca, "referral.endDate");
    if (!expiredTime) {
      return null;
    }

    let color = "#363636";
    const now = moment();
    const expiredMoment = moment(expiredTime);

    if (expiredMoment?.unix() === moment("1900-01-01T00:00:00").unix()) {
      return <div style={{ color, fontWeight: "Bold" }}>Indefinitely Valid</div>;
    }

    const nextMonth = now.clone().add(3, "month");
    if (nextMonth.isAfter(expiredMoment)) {
      color = "#E8903D";
    }

    if (now.isAfter(expiredMoment)) {
      color = "#DA3732";
    }

    return <div style={{ color, fontWeight: "Bold" }}>{displayDateTime(expiredMoment, "YYYY-MM-DD HH:mm")}</div>;
  };
  const caseManager = getCaseManagerById(ca, practitioners, practitionerPreferences);
  const caseManagerClassName = caseManager ? "dropdown is-hoverable is-right" : "";

  const logo = _.get(ca, "createdBy.logoLink", _.get(ca, "appointment.bookedBy.logoLink"));
  const sourceName = _.get(ca, "createdBy.name", _.get(ca, "appointment.bookedBy.name"));
  const sourceBgColor = _.get(ca, "createdBy.backgroundColor", _.get(ca, "appointment.bookedBy.backgroundColor"));
  const paymentConsent = _.get(ca, "appointment_t.payment_consent");
  const alerts = _.get(ca, "alerts", []);
  const isPatientDuplicated = alerts.filter((alert) => alert.code === "existing-coreplus-client").length > 0;
  const dob = moment(_.get(ca, "client.dateOfBirth", ""), "YYYY-MM-DD");
  const getNrrDisplayText = (ca) => {
    const nrr = _.get(ca, "appointment_t.cont_or_tbc", "");
    if (_.startsWith(nrr, "NRR X 1")) {
      return "NRR1";
    }
    if (_.startsWith(nrr, "NRR X 2")) {
      return "NRR2";
    }
    return null;
  };
  return (
    <tr {...rowProps}>
      <td>
        {_.get(ca, "needAttention", false) && (
          <span className="icon has-text-danger">
            <i className="fas fa-exclamation-triangle"></i>
          </span>
        )}
        <div>{_.get(ca, "client.ExternalId", "")}</div>
      </td>
      <td>
        <div>{_.get(ca, "category", "")}</div>
      </td>
      <td>
        {isPatientDuplicated && (
          <span className="icon is-small has-text-danger">
            <i className="fas fa-copy" />
          </span>
        )}
        {(logo || sourceName) && (
          <img
            src={logo}
            alt={sourceName}
            style={{ height: "18px", backgroundColor: sourceBgColor, color: "white", padding: "2px" }}
          />
        )}
        <div className={patientSnapformSubmitted ? "has-text-weight-bold has-text-primary" : ""}>{`${_.get(
          ca,
          "client.firstName",
          ""
        )} ${_.get(ca, "client.lastName", "")}`}</div>
        <div className="level">
          <div className="level-left">
            <div className="level-item">
              <span className="has-text-info">
                {_.isEmpty(_.get(ca, "client.phoneNumberMobile", ""))
                  ? _.get(ca, "client.phoneNumberHome", "")
                  : _.get(ca, "client.phoneNumberMobile", "")}
              </span>
            </div>
            {_.has(ca, "email_notifications.patient") && (
              <div className="level-item">
                <span className="icon is-small has-text-primary">
                  <i className="fas fa-envelope" />
                </span>
              </div>
            )}
          </div>
        </div>
      </td>
      <td>
        <div>{dob.format("DD/MM/YYYY")}</div>
        <div>{`(${moment().diff(dob, "years")} y.o)`}</div>
      </td>
      {/* Creation DT */}
      <td>
        {renderCreationTimeSection(ca, timeType)}
        <div>{renderManualSMSStatus(ca)}</div>
      </td>
      {/* Referer */}
      <td>
        <div>{getReferrerFullName(ca, referrers)}</div>
        <div className="referrer-info">
          {_.has(ca, "email_notifications.referrer") && (
            <span className="icon is-small has-text-primary">
              <i className="fas fa-envelope" />
            </span>
          )}
          {!_.isNil(ca?.referral_letters) && (
            <span className="icon is-small has-text-primary" onClick={handleDownloadReferralLetter(ca)}>
              {isDownloadingReferral ? <i className="fas fa-spinner fa-pulse" /> : <i className="fas fa-download" />}
            </span>
          )}
          {generateExpiredSection(ca)}
        </div>
      </td>
      {/* Case Manager */}
      <td>
        {(_.has(ca, "practitioner.id") || _.has(ca, "practitioner.practitionerId")) && (
          <div className={caseManagerClassName}>
            <div>{caseManager}</div>
            {caseManager && schedule && (
              <div className="dropdown-menu">
                <div className="dropdown-content px-2 is-size-7 is-radiusless has-background-info has-text-white">
                  {schedule}
                </div>
              </div>
            )}
          </div>
        )}
        {_.has(ca, "email_notifications.practitioner") && (
          <div>
            <span className="icon is-small has-text-primary">
              <i className="fas fa-envelope"></i>
            </span>
          </div>
        )}
      </td>
      {/* Speciality */}
      <td>
        {(_.has(ca, "practitioner.id") || _.has(ca, "practitioner.practitionerId")) && (
          <div>
            {specialistProfiles.find(({ id }) => ca.practitioner.practitionerId === id)?.specialties?.[0]?.name || ""}
          </div>
        )}
      </td>
      {/* Hospital */}
      <td>{renderHospitalColumn(ca)}</td>
      {/* APT Date */}
      <td>
        {_.has(ca, "appointment.startDateTime") && formatDateTime(ca.appointment.startDateTime, true, false, true)}
        <div>
          {getReceivedYesStatus(ca.id, clientAppointments, clientAppointments)}
          {renderIVRIcon(ca.id, clientAppointments)}
        </div>
      </td>
      {/* Bookings */}
      <td>
        <div className={taskManagerClassName}>
          <div className="dropdown-trigger">
            <div className="level" {...taskManagerEventProps} style={{ display: "inline-block", marginBottom: 0 }}>
              <div
                className={`level-item task-btn px-2 ${getTaskStatusClasses(_.get(ca, "appointment_t.status", ""))}`}
              >
                {_.find(WORKFLOW_APPT_STATUS, (status) => status.value === _.get(ca, "appointment_t.status", ""))
                  ?.label || "++"}
              </div>
            </div>
            <div className="level" {...taskManagerEventProps} style={{ marginBottom: 0 }}>
              <div className="level-left">
                <div className="level-item">{newOrReview}</div>
                <div className="level-item">{remainingWeeksToAppointment}</div>
                <div className="level-item">{getNrrDisplayText(ca)}</div>
              </div>
            </div>
            <div className="level" {...taskManagerEventProps} style={{ justifyContent: "start", marginBottom: 0 }}>
              {paymentConsent && (
                <span className="icon is-small has-text-success">
                  <i className="fas fa-dollar-sign" />
                </span>
              )}
              {_.get(ca, "appointment_t.urgent", false) && (
                <span className="icon has-text-danger-dark">
                  <i className="fas fa-exclamation-triangle" />
                </span>
              )}
              {_.get(ca, "appointment_t.triage", false) && (
                <span className="icon has-text-info-dark">
                  <i className="fas fa-stethoscope" />
                </span>
              )}
            </div>
            {_.get(ca, "appointment_t.biologics", "") && (
              <span className="icon has-text-info-dark">
                <i className="fas fa-capsules" />
              </span>
            )}
            {_.get(ca, "appointment_t.interpreter", "") && (
              <span className="icon has-text-info-dark">
                <i className="fas fa-headset" />
              </span>
            )}
            {_.get(ca, "appointment.appointmentType", "") === APPOINTMENT_TYPE.AT_HOME && (
              <span className="icon is-small has-text-info-dark">
                <i className="fas fa-home" />
              </span>
            )}
          </div>
          {_.get(ca, "appointment_t.notes", "") && (
            <div className="dropdown-menu">
              <div className="dropdown-content px-2 is-size-7 is-radiusless has-background-info has-text-white">
                {_.get(ca, "appointment_t.notes", "")}
              </div>
            </div>
          )}
        </div>
      </td>
      {/* Results */}
      <td>
        <div className="level" {...taskManagerEventProps} style={{ marginBottom: 0 }}>
          {/* Status button: hover shows notes, click opens Manage Tasks. Notes
              hover is scoped here (not the whole cell) so the synced indicator
              beside it never triggers the notes tooltip. */}
          <div className="level-item">
            <div className={`${isLastWorkflowRow ? "is-up " : ""}dropdown is-hoverable is-right`}>
              <div className="dropdown-trigger">
                <div className={preConsultTaskBtnClassName}>{preConsultTaskBtnLabel}</div>
              </div>
              {_.get(ca, "pre_consult_t.notes", "") && (
                <div className="dropdown-menu">
                  <div className="dropdown-content px-2 is-size-7 is-radiusless has-background-info has-text-white">
                    {_.get(ca, "pre_consult_t.notes", "")}
                  </div>
                </div>
              )}
            </div>
          </div>
          {_.get(ca, "pre_consult_t.urgent", false) && (
            <div className="level-item">
              <span className="icon has-text-danger-dark">
                <i className="fas fa-exclamation-triangle" />
              </span>
            </div>
          )}
          {syncedMedicalResults.length > 0 && (
            <div className="level-item">
              <SyncedResultsIndicator results={syncedMedicalResults} openUp={isLastWorkflowRow} />
            </div>
          )}
        </div>
      </td>
      {/* Fin */}
      <td>
        <div>{_.get(ca, "finNo", "")}</div>
      </td>
      {/* Nurse Check */}
      <td>
        <div>
          {_.get(ca, "gp_report_t.is_nurse_check", false) && (
            <span className="dropdown-content px-2 is-size-7 is-radiusless has-background-info has-text-white">
              Checked
            </span>
          )}
        </div>
      </td>
      {/* GP Report */}
      {renderGpReportCell({
        ca,
        isGPReportListEnabled,
        isDownloadingGPReport,
        handleClickRow,
        handleDownloadGPReport,
      })}
    </tr>
  );
};

// Treat Enter/Space on a focusable element with `role="button"` as a click,
// per WAI-ARIA practices. Used on the GP Report cells below so keyboard-only
// users can open the Outbound Letters tab.
const activateOnEnterOrSpace = (onActivate) => (e) => {
  if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
    e.preventDefault();
    e.stopPropagation();
    onActivate(e);
  }
};

// Clickable `<td>` that opens the patient panel on the Outbound Letters tab.
// Used in two visually distinct states (status pill / "View Letters" pending
// button) — kept as a single component so the keyboard/aria affordances can't
// drift between them.
const OutboundLettersTabCell = ({ caId, handleClickRow, className, ariaLabel, title, children }) => {
  // The History tab reuses this row without a row handler, so there is no
  // panel to open: show the same status, not clickable.
  if (!_.isFunction(handleClickRow)) {
    return (
      <td className={className}>
        <div className={className}>{children}</div>
      </td>
    );
  }
  const navigate = (e) => {
    e.stopPropagation();
    handleClickRow(caId, "gp_reports");
  };
  return (
    <td
      className={className}
      onClick={navigate}
      onKeyDown={activateOnEnterOrSpace(navigate)}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      style={{ cursor: "pointer" }}
      title={title}
    >
      <div className={className}>{children}</div>
    </td>
  );
};

// Workflow-row "GP Report" cell. Three display states:
//   1. `gp_report` set + status NC/QR/CP  -> coloured status pill
//      (Not Completed / Query / Completed) — same as legacy.
//   2. `gp_report` not set, but `gp_reports` has entries -> neutral
//      "View Letters (N)" button so the coordinator knows letters exist and
//      can pick one.
//   3. Nothing yet -> empty cell.
//
// On tenants with `isGPReportListEnabled` (e.g. Wide Bay) clicking the cell
// opens the patient panel on the Outbound Letters tab; on legacy tenants the
// icon keeps its single-file download behaviour.
const renderGpReportCell = ({
  ca,
  isGPReportListEnabled,
  isDownloadingGPReport,
  handleClickRow,
  handleDownloadGPReport,
}) => {
  const displayText = getGpReportCellDisplayText(ca);
  const hasGpReport = _.has(ca, "gp_report");
  const outboundLetterCount = getVerifiedOutboundLetters(ca).length;
  const hasOutboundLetters = outboundLetterCount > 0;

  // State 1: a GP report is selected — show the coloured status pill.
  if (hasGpReport) {
    const cellColorClass = getGpStatusClasses(_.get(ca, "gp_report_t.status", ""));

    if (isGPReportListEnabled) {
      return (
        <OutboundLettersTabCell
          caId={ca.id}
          handleClickRow={handleClickRow}
          className={cellColorClass}
          ariaLabel="View outbound letters"
          title="View outbound letters"
        >
          <span className="icon is-small has-text-primary-light">
            <i className="fas fa-list" />
          </span>
          {displayText}
        </OutboundLettersTabCell>
      );
    }

    return (
      <td className={cellColorClass}>
        <div className={cellColorClass}>
          <span
            className="icon is-small has-text-primary-light"
            onClick={handleDownloadGPReport(ca)}
            title="Download GP report"
          >
            {isDownloadingGPReport ? <i className="fas fa-spinner fa-pulse" /> : <i className="fas fa-download" />}
          </span>
          {displayText}
        </div>
      </td>
    );
  }

  // State 2: outbound letters exist but none has been picked as the GP report
  // yet. Mirrors the visual weight of the status pills so the column reads
  // consistently — coloured background, icon + label, whole cell clickable.
  if (isGPReportListEnabled && hasOutboundLetters) {
    return (
      <OutboundLettersTabCell
        caId={ca.id}
        handleClickRow={handleClickRow}
        className="has-background-info has-text-white"
        ariaLabel={`View outbound letters and set the GP report (${outboundLetterCount})`}
        title="View outbound letters and set the GP report"
      >
        <span className="icon is-small has-text-primary-light">
          <i className="fas fa-list" />
        </span>
        {displayText}
      </OutboundLettersTabCell>
    );
  }

  // State 3: nothing to show.
  return <td />;
};

export default WorkflowTableItem;
